import ApiServer from "../ApiServer.ts";
import FizConfig from "../FizConfig.ts";
import * as FwDatas from "../FwDatas.ts";
import * as amplify from "../util/amplify.ts";
import {BaseCache,ListCache,SyncCache, TranslationCache} from "../lib/SyncCache.ts";
import { protoExtend } from "../lib/ObjectUtil.ts";
import { CalendarBean, CalendarGroupBean, IEvent, TaskBean, TimetableBean } from "./fizapilib.auto.ts";
import type { EventOccurrence } from "./decoders_connected.ts";

import { TaskCache,TaskCategoryCache,TaskListCache} from "./objects/TaskBeans.ts"
import { ContactCache } from "../contact/ContactCache.ts";
import { PlaceCache } from "./objects/MapBeans.ts";

// Augmentation of the generated beans with the properties added dynamically below
declare module "./fizapilib.auto.ts" {
  interface CalendarGroupBean {
    /** metaId of every calendar the user wants to see, sub calendars of the activated folders included */
    activatedIds?: string[];
    /** every calendar of the group, flattened */
    list?: CalendarBean[];
    byId?: {[metaId: string]: CalendarBean};
  }
  interface TimetableBean {
    /** back reference to the calendar carrying this timetable */
    calendar?: CalendarBean;
  }
  interface IEvent {
    /** an occurrence of a special day calendar, it cannot be edited */
    isSpecialDaysEvent?: boolean;
    /** a task with a due date, displayed in the calendar through buildTaskAsEventCache() */
    isTaskEvent?: boolean;
  }
}

/** google and outlook credentials both carry the calendars they are linked to */
interface CredentialWithCalendars {
  linked: CalendarBean[];
}

const evtcallistprepared = ApiServer.evtcallist.prepare(true,true,true,true,true,true,true,function(res: CalendarGroupBean){
  var calendarList: CalendarBean[] = ([] as CalendarBean[]).concat(res.mine).concat(res.external?res.external:[]).concat(res.families).concat(res.timetables).concat(res.budgets).filter(cal=>cal!==undefined);
  var calendarByid: {[metaId: string]: CalendarBean} = {};
  res.activatedIds = [];
  ([] as CredentialWithCalendars[]).concat(res.googles).concat(res.outlooks).forEach(credential=>{
    calendarList = calendarList.concat(credential.linked);
    credential.linked.forEach(cal=>{
      cal.rights.canShare = true;
    });
  });
  if(res.external){
    res.external.forEach(cal=>{
      cal.rights.canShare = true;
    });
  }
  calendarList.forEach(cal=>{
    calendarByid[cal.metaId] = cal;
    if(cal.activated && !cal.calendars){
      res.activatedIds.push(cal.metaId)
    }
    if(cal.calendars){
      cal.calendars.forEach(subCalendar=>{
        calendarList.push(subCalendar)
        calendarByid[subCalendar.metaId] = subCalendar
        if(cal.activated && subCalendar.activated){
          res.activatedIds.push(subCalendar.metaId)
        }
      })
    }
  });
  res.timetables.forEach(c=>{
    //set a back reference to Calendarbean
    c.timetable.calendar = c
    //cache timetable
    FwDatas.cache(c.timetable)
  })
  //TODO verify if this this list is used and if not remove it
  res.list = calendarList;
  res.byId = calendarByid;
  return res;
})

class CalListCacheClass extends BaseCache<CalendarGroupBean>{
  syncCacheById : {[key:string]:ListCache<IEvent>}
  constructor(){
    super(evtcallistprepared)
    this.syncCacheById = {};
  }
  //get an element in the current cache out of date or not
  getCache(metaId :string): ListCache<IEvent> {
    if(!this.syncCacheById[metaId]){
      if(metaId.indexOf('calendarFolder/')===0 || metaId.indexOf('calendarFolderOtherFamilies/')===0){
        this.syncCacheById[metaId] = new ListCache<IEvent>(async ()=>{return []})
      }else if(metaId.indexOf('calendarTask/')===0){
        this.syncCacheById[metaId] = buildTaskAsEventCache(metaId)
      }else{
        if(metaId.indexOf('calendarSpecialDays/')===0){
          var
            rawCache = new SyncCache<IEvent>(ApiServer.evtsync.prepare(null,null,metaId)),
            cache = new TranslationCache<IEvent,IEvent>(rawCache,(events)=>{
              var
                calendar = this.datas.byId[metaId],
                exceptions: IEvent[] = [],
                masterEvents = events.filter(ev=>{
                  ev.calendar = calendar;
                  ev.isSpecialDaysEvent = true;
                  if(ev.recurrencyExceptionOfId) {
                    exceptions.push(ev)
                    return false
                  }
                  ev.recurrencyExceptionMap = new Map()
                  return true;
                })
              exceptions.forEach(ev=>{
                var masterEvent = rawCache.get(ev.recurrencyExceptionOfId)
                if(masterEvent){
                  ev.eventMasterId = masterEvent.metaId;
                  masterEvent.recurrencyExceptionMap.set(ev.occurenceIndex,ev);
                  //recopy what needs to be
                  ev.recurrencyDescriptor = masterEvent.recurrencyDescriptor
                  ev.reminderList = masterEvent.reminderList
                }else{
                  console.log('masterEvent of '+ev.metaId+" does not exists")
                }
              })
              return masterEvents;
            })
            cache.addDependency(CalListCache, ContactCache, TaskListCache, PlaceCache)
            this.syncCacheById[metaId] = cache;
        }else{
          this.syncCacheById[metaId] = new SyncCache<IEvent>(ApiServer.evtsync.prepare(null,null,metaId))

        }
      }
    }
    return this.syncCacheById[metaId];
  }
}

/** the IEvent fields the calendar views read and which a task does not carry natively */
interface TaskAsEventExtension {
  isAttendee(this: TaskAsEvent, accountId: number): boolean;
  attendeeIds: number[];
  calendarId: string;
  draggable: boolean;
  colorDB?: string;
  isTaskEvent: boolean;
}
/** a task displayed in the calendar, prototype linked to its task so it carries every task field too */
type TaskAsEvent = TaskBean & TaskAsEventExtension;

function buildTaskAsEventCache(calendarId :string): ListCache<IEvent> {
  const cache = new TranslationCache<TaskAsEvent,TaskBean>(TaskCache,function(tasks){
    const
      result: TaskAsEvent[] = [];

    tasks.forEach((task)=>{
      if(task.dueDate){
        var extension: TaskAsEventExtension = {
          isAttendee(this: TaskAsEvent, accountId: number): boolean {
            return this.assignee.find((assignee)=>{
              return accountId===assignee.accountId;
            })!==undefined;
          },
          attendeeIds:task.assignee.map(assignee=>assignee.accountId),
          calendarId,
          draggable : task.editable,
          colorDB : undefined,
          isTaskEvent : true
        };
        //taskAsEvent.metaId = task.metaId.replace('task/','taskAsEvent/')
        if(task.assignee.length===1){
          extension.colorDB = task.assignee[0].color
        }
        //disable drag n drop for recurring task
        if(task.recurrencyDescriptor.recurrency!=='NONE'){
          extension.draggable = false;
        }
        result.push(protoExtend(task,extension));
      }
    });
    return result;
  })
  //a task as event only carries the IEvent fields the calendar views actually read
  return cache as unknown as ListCache<IEvent>;
}

const CalListCache = new CalListCacheClass();

const EventStore = {
  invalidate(calendarIdFilter: (calendarId: string)=>boolean = (t=>true)){
    Object.entries(CalListCache.syncCacheById).forEach(([key,value])=>{
      if(calendarIdFilter(key)){
        value.invalidate()
      }
    })
  },
  async getSelectedCalendarIds(): Promise<string[]>{
    return CalListCache.need().then(calendarBean=>{
      return calendarBean.activatedIds
    })
  },

  async getFor(selectedCalendarIds :string[]) :Promise<IEvent[]>{
    if(!selectedCalendarIds) {
      selectedCalendarIds = await this.getSelectedCalendarIds()
    }

    ApiServer.setGroupOptions({timeout:60000}); //60sec for calendar instead of 20s because of a user who has 13000 event

    const
      arrayOfArray = await Promise.all(
        selectedCalendarIds.map(calendarId=>CalListCache.getCache(calendarId).need())
      ),
      result = arrayOfArray.flat()

    result.forEach(event=>{
      event.calendar = CalListCache.datas.byId[event.calendarId];
    })
    return result;

  },
  async loadEventsBySync(selectedCalendarIds :string[],selectedMembersIds :number[],range :{start:Date, end:Date}): Promise<EventOccurrence[]>{
    const
      allevents = await this.getFor(selectedCalendarIds),
      intervalEvent: EventOccurrence[] = [];
    allevents.forEach((event)=>{
      //handle the filter of attendee
      if(selectedMembersIds.length>0){
        if(selectedMembersIds.find(memberId=>event.isAttendee(memberId))===undefined){
          return;
        }
      }
      event.listInterval(range).forEach(eventInstance=>{
        FwDatas.cache(eventInstance)
        intervalEvent.push(eventInstance);
        eventInstance.colorUI = eventInstance.color = (event.colorDB || event.colorAttendee || event.calendar.color || FizConfig.defaultColor);
      });
    });
    return intervalEvent;
  }
}
//this is only for the color
amplify.subscribe("familyScopeChange",function(){
  CalListCache.invalidate();
  EventStore.invalidate();
});

export {CalListCache,EventStore};
